import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { AsyncError, Banner, EmptyState, Loading } from './states';

interface Agreement {
  id: string;
  agreementRef: string;
  status: string;
  documentHash: string;
  signedBy: string;
  summary: string[];
  activatedAt: string;
  expiresAt: string | null;
  terminationReason: string | null;
  document: {
    participants: Array<{ organization: string; role: string }>;
    revision: number;
  };
}

interface Telemetry {
  visibility: string;
  data: Record<string, unknown>;
}

interface ChangeRequest {
  id: string;
  requestedBy: string;
  status: string;
  note: string;
  responseNote: string | null;
  expandsAccess: boolean;
  changes: Array<{ field: string; from: string; to: string; direction: string }>;
}

interface Binding {
  deviceId: string;
  name: string;
}

interface Device {
  id: string;
  name: string;
}

interface Page<T> {
  items: T[];
}

interface Plan {
  deployable: boolean;
  imports: Array<{ cidr: string; natRequired: boolean; description: string }>;
  exports: Array<{ cidr: string; natRequired: boolean; description: string }>;
  firewall: Array<{
    destination: string;
    protocol: string;
    port: number | null;
    description: string;
  }>;
  transitPermitted: boolean;
  findings: Array<{ code: string; severity: string; detail: string }>;
}

interface Verification {
  valid: boolean;
  findings: Array<{ code: string; ok: boolean; detail: string }>;
}

/**
 * Federated connection agreements.
 *
 * An agreement is what two organizations actually agreed, frozen and signed. The
 * summary shown here is derived from the terms rather than from anything either
 * party wrote about them, for the same reason the invitation risk summary is:
 * somebody reading this months later needs what the agreement does, not what it
 * was described as.
 *
 * Verification is offered as an explicit action rather than a badge, because a
 * green tick that was computed at render time and a green tick that means the
 * signature was just checked look identical and are not the same claim.
 */
export function AgreementsPanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const mayWrite = can('federation:write');

  const agreements = useAsync<Agreement[]>(
    () => api.get<Agreement[]>('/federation/agreements'),
    [],
  );
  const [checked, setChecked] = useState<Record<string, Verification>>({});
  const [plans, setPlans] = useState<Record<string, Plan>>({});
  const [bindings, setBindings] = useState<Record<string, Binding[]>>({});
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [requests, setRequests] = useState<Record<string, ChangeRequest[]>>({});
  const [health, setHealth] = useState<Record<string, Telemetry>>({});
  const devices = useAsync<Page<Device>>(
    () => api.get<Page<Device>>('/edge-devices?limit=200'),
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      agreements.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  const verify = (id: string): void => {
    setError(null);
    api
      .get<Verification>(`/federation/agreements/${id}/verify`)
      .then((r) => setChecked((prior) => ({ ...prior, [id]: r })))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      );
  };

  const showPlan = (id: string): void => {
    setError(null);
    api
      .get<Plan>(`/federation/agreements/${id}/plan`)
      .then((r) => setPlans((prior) => ({ ...prior, [id]: r })))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      );
  };

  const loadBindings = (id: string): void => {
    api
      .get<Binding[]>(`/federation/agreements/${id}/bindings`)
      .then((r) => setBindings((prior) => ({ ...prior, [id]: r })))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      );
  };

  const bind = (id: string): void => {
    const deviceId = chosen[id];
    if (!deviceId) return;
    void run(async () => {
      await api.post(`/federation/agreements/${id}/bindings`, { deviceId });
      loadBindings(id);
    });
  };

  const unbind = (id: string, deviceId: string): void => {
    void run(async () => {
      await api.del(`/federation/agreements/${id}/bindings/${deviceId}`);
      loadBindings(id);
    });
  };

  const loadRequests = (id: string): void => {
    api
      .get<ChangeRequest[]>(`/federation/agreements/${id}/change-requests`)
      .then((r) => setRequests((prior) => ({ ...prior, [id]: r })))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      );
  };

  const decide = (agreementId: string, requestId: string, action: 'approve' | 'reject'): void => {
    void run(async () => {
      await api.post(`/federation/change-requests/${requestId}/${action}`, {});
      loadRequests(agreementId);
    });
  };

  const loadHealth = (id: string): void => {
    api
      .get<Telemetry>(`/federation/agreements/${id}/telemetry`)
      .then((r) => setHealth((prior) => ({ ...prior, [id]: r })))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : t('errors.unexpected')),
      );
  };

  const terminate = (id: string): void => {
    void run(() =>
      api.post(`/federation/agreements/${id}/terminate`, {
        reason: t('agreements.endedFromConsole'),
      }),
    );
  };

  return (
    <div className="card">
      <h2>{t('agreements.title')}</h2>
      <p>{t('agreements.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}

      {agreements.loading ? (
        <Loading />
      ) : agreements.error ? (
        <AsyncError error={agreements.error} onRetry={agreements.reload} />
      ) : !agreements.data || agreements.data.length === 0 ? (
        <EmptyState hint={t('agreements.none')} />
      ) : (
        agreements.data.map((a) => {
          const result = checked[a.id];
          return (
            <div className="card" key={a.id}>
              <h3>{a.document.participants.map((p) => p.organization).join(' ↔ ')}</h3>
              <dl className="detail-grid">
                <dt>{t('agreements.status')}</dt>
                <dd>{t(`agreements.states.${a.status}`)}</dd>
                <dt>{t('agreements.reference')}</dt>
                <dd>
                  <code>{a.agreementRef}</code>
                </dd>
                <dt>{t('agreements.signedBy')}</dt>
                <dd>
                  <code>{a.signedBy}</code>
                </dd>
                <dt>{t('agreements.activated')}</dt>
                <dd>{new Date(a.activatedAt).toLocaleString()}</dd>
                <dt>{t('agreements.expires')}</dt>
                <dd>
                  {a.expiresAt ? new Date(a.expiresAt).toLocaleString() : t('agreements.noExpiry')}
                </dd>
              </dl>

              <h4>{t('agreements.whatItPermits')}</h4>
              <ul>
                {a.summary.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>

              {a.terminationReason ? (
                <p>
                  {t('agreements.endedBecause')} {a.terminationReason}
                </p>
              ) : null}

              {result ? (
                <Banner variant={result.valid ? 'info' : 'error'}>
                  {result.valid ? t('agreements.verified') : t('agreements.tampered')}
                  <ul>
                    {result.findings
                      .filter((f) => !f.ok)
                      .map((f) => (
                        <li key={f.code}>{f.detail}</li>
                      ))}
                  </ul>
                </Banner>
              ) : null}

              {plans[a.id] ? (
                <>
                  <h4>{t('agreements.planTitle')}</h4>
                  <p>{t('agreements.planIntro')}</p>
                  {!plans[a.id]?.deployable ? (
                    <Banner variant="error">{t('agreements.planBlocked')}</Banner>
                  ) : null}
                  <dl className="detail-grid">
                    <dt>{t('agreements.accepts')}</dt>
                    <dd>
                      {plans[a.id]?.imports.length === 0
                        ? t('agreements.nothing')
                        : plans[a.id]?.imports
                            .map((i) => `${i.cidr}${i.natRequired ? ' (NAT)' : ''}`)
                            .join(', ')}
                    </dd>
                    <dt>{t('agreements.advertises')}</dt>
                    <dd>
                      {plans[a.id]?.exports.length === 0
                        ? t('agreements.nothing')
                        : plans[a.id]?.exports.map((e) => e.cidr).join(', ')}
                    </dd>
                    <dt>{t('agreements.transit')}</dt>
                    <dd>
                      {plans[a.id]?.transitPermitted
                        ? t('agreements.transitPermitted')
                        : t('agreements.transitDenied')}
                    </dd>
                  </dl>
                  <ul>
                    {plans[a.id]?.findings
                      .filter((f) => f.severity !== 'ok')
                      .map((f) => (
                        <li key={f.code}>{f.detail}</li>
                      ))}
                  </ul>
                </>
              ) : null}

              {requests[a.id] !== undefined && (requests[a.id]?.length ?? 0) > 0 ? (
                <>
                  <h4>{t('agreements.changeRequests')}</h4>
                  {requests[a.id]?.map((cr) => (
                    <div className="card" key={cr.id}>
                      <p>
                        {t(`agreements.requestedBy.${cr.requestedBy}`)} ·{' '}
                        {t(`agreements.requestStates.${cr.status}`)}
                      </p>
                      {cr.expandsAccess ? (
                        <Banner variant="warning">{t('agreements.requestExpands')}</Banner>
                      ) : null}
                      <ul>
                        {cr.changes.map((c) => (
                          <li key={`${c.field}-${c.to}`}>
                            {c.field}: {c.from} → {c.to} (
                            {t(`invitations.directions.${c.direction}`)})
                          </li>
                        ))}
                      </ul>
                      {cr.note ? <p>{cr.note}</p> : null}
                      {cr.responseNote ? <p>{cr.responseNote}</p> : null}
                      {mayWrite && cr.status === 'SUBMITTED' ? (
                        <div className="row-actions">
                          <button
                            type="button"
                            className="btn"
                            disabled={busy}
                            onClick={() => decide(a.id, cr.id, 'approve')}
                          >
                            {t('agreements.approveChange')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--danger"
                            disabled={busy}
                            onClick={() => decide(a.id, cr.id, 'reject')}
                          >
                            {t('agreements.rejectChange')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </>
              ) : null}

              {health[a.id] ? (
                <>
                  <h4>{t('agreements.theirHealth')}</h4>
                  <p>
                    {t('agreements.visibilityIs')}{' '}
                    {t(`agreements.visibility.${health[a.id]?.visibility}`)}
                  </p>
                  {Object.keys(health[a.id]?.data ?? {}).length === 0 ? (
                    <EmptyState hint={t('agreements.noVisibility')} />
                  ) : (
                    <dl className="detail-grid">
                      {Object.entries(health[a.id]?.data ?? {}).map(([field, value]) => (
                        <div key={field}>
                          <dt>{field}</dt>
                          <dd>{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </>
              ) : null}

              <h4>{t('agreements.appliedTo')}</h4>
              <p>{t('agreements.appliedToIntro')}</p>
              {bindings[a.id] === undefined ? (
                <button type="button" className="btn btn--ghost" onClick={() => loadBindings(a.id)}>
                  {t('agreements.showBindings')}
                </button>
              ) : bindings[a.id]?.length === 0 ? (
                <EmptyState hint={t('agreements.noBindings')} />
              ) : (
                <ul>
                  {bindings[a.id]?.map((b) => (
                    <li key={b.deviceId}>
                      {b.name}{' '}
                      {mayWrite ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          disabled={busy}
                          onClick={() => unbind(a.id, b.deviceId)}
                        >
                          {t('agreements.unbind')}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}

              {mayWrite && bindings[a.id] !== undefined && a.status === 'ACTIVE' ? (
                <div className="form form--inline">
                  <label className="field">
                    <span>{t('agreements.chooseEdge')}</span>
                    <select
                      value={chosen[a.id] ?? ''}
                      onChange={(e) => setChosen((prior) => ({ ...prior, [a.id]: e.target.value }))}
                    >
                      <option value="">{t('agreements.chooseEdgePrompt')}</option>
                      {(devices.data?.items ?? []).map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || !chosen[a.id]}
                    onClick={() => bind(a.id)}
                  >
                    {t('agreements.bind')}
                  </button>
                </div>
              ) : null}

              <div className="row-actions">
                <button type="button" className="btn btn--ghost" onClick={() => verify(a.id)}>
                  {t('agreements.verify')}
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => showPlan(a.id)}>
                  {t('agreements.showPlan')}
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => loadRequests(a.id)}>
                  {t('agreements.showChanges')}
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => loadHealth(a.id)}>
                  {t('agreements.showHealth')}
                </button>
                {mayWrite && a.status === 'ACTIVE' ? (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() =>
                      void run(() => api.post(`/federation/agreements/${a.id}/suspend`, {}))
                    }
                  >
                    {t('agreements.suspend')}
                  </button>
                ) : null}
                {mayWrite && a.status === 'SUSPENDED' ? (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() =>
                      void run(() => api.post(`/federation/agreements/${a.id}/resume`, {}))
                    }
                  >
                    {t('agreements.resume')}
                  </button>
                ) : null}
                {mayWrite && a.status !== 'TERMINATED' ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--danger"
                    disabled={busy}
                    onClick={() => terminate(a.id)}
                  >
                    {t('agreements.terminate')}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
